import { initializeApp } from '../libs/init/exec.init';
import { canonicalizeAuthTrailingSlash } from '../shared/routerUtils/canonicalizeAuthTrailingSlash';

// Redirect a slashless auth entry URL (e.g. /v2/auth/account/login) to its
// trailing-slash form before anything else runs, so the router and Frontegg
// only ever read the URL shape Frontegg matches. No-op off the auth routes, in
// development, and during the static-export prerender.
canonicalizeAuthTrailingSlash();

await initializeApp();

import { useEffect, useState, type ReactElement, type ReactNode } from 'react';
import Head from 'next/head';
import {
  createBrowserRouter,
  type DataRouter,
  RouterProvider,
} from 'react-router-dom';
import { type NextPage } from 'next';
import { routes } from '../routing/routes';
import { getAppBasename } from '../hooks/useAppRouter';
import { bootstrapNextRouterFromStaticSiteEntry } from '../shared/routerUtils/bootstrapNextRouterFromStaticSiteEntry';

export type NextPageWithLayout<P = unknown, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement) => ReactNode;
};

/**
 * React Router 7 owns routing for the whole app. The browser router is created
 * lazily on the client only — `createBrowserRouter` reads `window.history`, so
 * it must not run during the static-export prerender (where there is no
 * `window`).
 */
let browserRouter: DataRouter | null = null;

const getBrowserRouter = (): DataRouter => {
  browserRouter ??= createBrowserRouter(routes, {
    basename: getAppBasename(),
  });
  return browserRouter;
};

/**
 * Mount sequence on a full-page load (nginx → static-site-router HTML):
 *
 * 1. `bootstrapNextRouterFromStaticSiteEntry` — appease Next's client runtime so
 *    it soft-syncs to the browser URL (PROD-9089). RR7 owns the UI; this is not
 *    app routing. Removed when Vite drops Next's client router (PROD-8714).
 * 2. `RouterProvider` — RR7 reads `window.location` and renders the app.
 *
 * RR7 is deferred to a post-hydration effect so the first client render matches
 * the static-export prerender (empty shell) and avoids a hydration mismatch.
 */
function CustomApp(): ReactElement {
  const [router, setRouter] = useState<DataRouter | null>(null);

  useEffect(() => {
    void bootstrapNextRouterFromStaticSiteEntry().finally(() => {
      setRouter(getBrowserRouter());
    });
  }, []);

  return (
    <>
      <Head>
        <meta name='viewport' content='width=device-width, initial-scale=1' />
      </Head>
      {router ? (
        <RouterProvider router={router} />
      ) : (
        <div style={{ height: '100%', width: '100%' }} />
      )}
    </>
  );
}

export default CustomApp;
